import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import type { Build, BuildInsert, BuildUpdate } from '../lib/database.types'

const BUILDS_KEY = ['builds']
const PRODUCTS_KEY = ['products']

export function useBuilds() {
  return useQuery({
    queryKey: BUILDS_KEY,
    queryFn: async (): Promise<Build[]> => {
      const { data, error } = await supabase
        .from('builds')
        .select('*')
        .order('status', { ascending: true }) // in_progress first alphabetically
        .order('started_at', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })
}

export function useCreateBuild() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: Omit<BuildInsert, 'user_id'>): Promise<Build> => {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) throw new Error('not authenticated')
      const { data, error } = await supabase
        .from('builds')
        .insert({ ...input, user_id: user.id })
        .select('*')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BUILDS_KEY })
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEY })
    },
  })
}

export function useUpdateBuild() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & BuildUpdate): Promise<Build> => {
      // Guard the same invariant useDeleteBuild does: an update to either
      // 'units' or 'status' on a completed build must not drop available
      // inventory below already-sold units. Without this, manually editing
      // units down (or marking completed -> in_progress / abandoned) can
      // make useProductInventory go negative.
      const isInventoryAffecting =
        updates.units !== undefined || updates.status !== undefined
      if (isInventoryAffecting) {
        const { data: thisBuild, error: readErr } = await supabase
          .from('builds')
          .select('product_id, units, status')
          .eq('id', id)
          .single()
        if (readErr) throw readErr

        if (thisBuild) {
          // Compute remaining produced after the proposed update.
          const { data: otherBuilds } = await supabase
            .from('builds')
            .select('id, units')
            .eq('product_id', thisBuild.product_id)
            .eq('status', 'completed')
          const remainingFromOthers = (otherBuilds ?? [])
            .filter((b) => b.id !== id)
            .reduce((s, b) => s + b.units, 0)
          const nextStatus = (updates.status ?? thisBuild.status) as Build['status']
          const nextUnits = updates.units ?? thisBuild.units
          const remainingAfter =
            nextStatus === 'completed' ? remainingFromOthers + nextUnits : remainingFromOthers

          // Sum non-refunded sold line items for this product.
          const { data: lineItems } = await supabase
            .from('sale_line_items')
            .select('quantity, sale_id')
            .eq('product_id', thisBuild.product_id)
          const saleIds = Array.from(new Set((lineItems ?? []).map((li) => li.sale_id)))
          let refundedSet = new Set<string>()
          if (saleIds.length > 0) {
            const { data: sales } = await supabase
              .from('sales')
              .select('id, is_refunded')
              .in('id', saleIds)
            refundedSet = new Set(
              (sales ?? []).filter((s) => s.is_refunded).map((s) => s.id),
            )
          }
          const unitsSold = (lineItems ?? [])
            .filter((li) => !refundedSet.has(li.sale_id))
            .reduce((s, li) => s + li.quantity, 0)

          if (unitsSold > remainingAfter) {
            const phantom = unitsSold - remainingAfter
            throw new Error(
              `Can't apply that change — ${phantom} unit${phantom === 1 ? '' : 's'} of this product have already been sold. Refund those sales first.`,
            )
          }
        }
      }

      const { data, error } = await supabase
        .from('builds')
        .update(updates)
        .eq('id', id)
        .select('*')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BUILDS_KEY })
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEY })
    },
  })
}

export function useDeleteBuild() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      // Block deletion that would push inventory negative.
      // Only completed builds contribute units, so non-completed deletes are
      // always safe; completed ones need the orphan check.
      const { data: thisBuild, error: buildErr } = await supabase
        .from('builds')
        .select('product_id, units, status')
        .eq('id', id)
        .single()
      if (buildErr) throw buildErr

      if (thisBuild && thisBuild.status === 'completed') {
        // Total units of this product currently in stock (after delete)
        // is sum of OTHER completed builds.
        const { data: completedBuilds, error: cbErr } = await supabase
          .from('builds')
          .select('id, units')
          .eq('product_id', thisBuild.product_id)
          .eq('status', 'completed')
        if (cbErr) throw cbErr

        const remainingAfterDelete = (completedBuilds ?? [])
          .filter((b) => b.id !== id)
          .reduce((sum, b) => sum + b.units, 0)

        // Units sold via non-refunded sales of this product.
        const { data: lineItems, error: liErr } = await supabase
          .from('sale_line_items')
          .select('quantity, sale_id')
          .eq('product_id', thisBuild.product_id)
        if (liErr) throw liErr

        const saleIds = Array.from(new Set((lineItems ?? []).map((li) => li.sale_id)))
        let refundedSet = new Set<string>()
        if (saleIds.length > 0) {
          const { data: sales, error: saleErr } = await supabase
            .from('sales')
            .select('id, is_refunded')
            .in('id', saleIds)
          if (saleErr) throw saleErr
          refundedSet = new Set((sales ?? []).filter((s) => s.is_refunded).map((s) => s.id))
        }

        const unitsSold = (lineItems ?? [])
          .filter((li) => !refundedSet.has(li.sale_id))
          .reduce((sum, li) => sum + li.quantity, 0)

        if (unitsSold > remainingAfterDelete) {
          const phantom = unitsSold - remainingAfterDelete
          throw new Error(
            `Can't delete — ${phantom} unit${phantom === 1 ? '' : 's'} from this build ${phantom === 1 ? 'has' : 'have'} already been sold. Refund those sales first, or mark this build as abandoned instead of deleting.`,
          )
        }
      }

      const { error } = await supabase.from('builds').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BUILDS_KEY })
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEY })
    },
  })
}

// Convenience: mark a build complete (sets status + completed_at)
export function useCompleteBuild() {
  const update = useUpdateBuild()
  return {
    ...update,
    mutate: (id: string) =>
      update.mutate({ id, status: 'completed', completed_at: new Date().toISOString() }),
    mutateAsync: (id: string) =>
      update.mutateAsync({ id, status: 'completed', completed_at: new Date().toISOString() }),
  }
}
